// @optimyl/ui — the Tailwind classes for a form field, both tones in one file.
//
// Two tones because the apps are different products: brand (public, phone-sized) and internal (dense,
// inside a Salesforce modal). Only the look differs.
//
// Every tone's focus ring MUST clear 3:1 against its field (WCAG 2.1 non-text contrast). That rule is why
// this file is shared; both apps have shipped a ring under it once. Rationale: docs/app-boundaries.md.

import type { FieldState } from "./fieldState";

/** Which visual language to draw in — not which app. A third app joins one of these, it does not add one. */
export type FieldTone = "brand" | "internal";

interface Tone {
  base: string;
  /** Per state: the border, any fill, and the focus ring that state draws. */
  state: Record<FieldState, string>;
  label: string;
}

const tones: Record<FieldTone, Tone> = {
  brand: {
    base: "w-full rounded-lg border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-green-dark focus:border-brand-green",
    state: {
      default: "border-gray-300 bg-white",
      prefilled: "border-success bg-success-bg",
      mismatch: "border-warning bg-warning-bg",
      error: "border-danger bg-danger-bg",
      readonly: "border-gray-200 bg-gray-50 text-gray-700",
    },
    label: "block text-sm font-medium text-gray-700 mb-1",
  },
  internal: {
    base: "w-full rounded-md border px-3 py-2 text-sm text-slate-800 focus:outline-none focus-visible:ring-2",
    // default and error set no background: they must render exactly what the paper review app shipped.
    // The ring is per state so it reinforces the error rather than fighting it.
    state: {
      default: "border-slate-300 focus-visible:ring-sky-600",
      prefilled: "border-emerald-500 bg-emerald-50 focus-visible:ring-sky-600",
      mismatch: "border-amber-500 bg-amber-50 focus-visible:ring-amber-600",
      error: "border-red-500 focus-visible:ring-red-600",
      readonly: "border-slate-200 bg-slate-50 text-slate-600 focus-visible:ring-sky-600",
    },
    label: "block text-sm font-medium text-slate-700 mb-1",
  },
};

export function inputClass(state: FieldState, tone: FieldTone): string {
  return `${tones[tone].base} ${tones[tone].state[state]}`;
}

export function labelClass(tone: FieldTone): string {
  return tones[tone].label;
}
